import { compose, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchBusinessDefaults, fetchIndexDefaults, fetchInitContext } from './thunks'
import { toast } from 'react-toastify'
import type { DriverListType } from '@view/contexts/FodContainerContext/types'
import type { RootState } from '..'
import type { ContextDropDowns } from './types'
import { setPoolFleetVehicles } from './resVehicleSlice'
import { formattedDriverBespokeFields } from '@view/components/forms/EditDriverForm/utils'

export const initialState: ContextDropDowns = {
	account: {
		businessId: '',
		businessName: '',
		businesses: [{ text: '', value: '' }],
	},
	booker: {},
	driver: {
		maximum: 10,
		driverId: '',
		drivers: [],
		driversInAddressSearch: [],
		newDrivers: [],
		existingDrivers: [],
		driversBespokeFields: {},
	},
	carClubDriver: null,
	selectedRentalTab: {
		name: '',
		tariffs: [],
	},
	availableTariffs: {
		panelIds: {},
	},
	tariffBandsAvailable: true,
	activeDropdown: null,
	poolFleetAvailable: false,
}

export const contextDropDownsSlice = createSlice({
	name: 'accountBookerDriver',
	initialState,
	reducers: {
		addAlertsToDriver(state, action) {
			const { alerts, selectedDriverId } = action.payload

			state.driver.drivers = state.driver.drivers.map((driver, i) => {
				for (const property in alerts) {
					if (driver.driverId == selectedDriverId) {
						driver.alerts = { ...driver.alerts, [`${property}${i + 1}`]: alerts[property] }
					}
				}

				return driver
			})
		},
		setDriversInAddressSearch(state, action) {
			state.driver.driversInAddressSearch = action.payload
		},
		updateNewDriverDetails(state, action) {
			function updateDrivers(drivers) {
				return drivers.map(driver => {
					if (driver.driverId == action.payload?.driverId) {
						driver = { ...driver, ...action.payload, name: `${action.payload.firstName} ${action.payload.surname}` }
					}
					return driver
				})
			}

			state.driver.drivers = updateDrivers(state.driver.drivers)
			state.driver.newDrivers = updateDrivers(state.driver.newDrivers)
		},
		updateBusiness(state, action) {
			state.account.businessId = action.payload.businessId
			state.account.businessName = action.payload.businessName
		},
		addBooker(state, action) {
			const booker = { ...action.payload, driverId: action.payload?.id }
			state.booker = booker
		},
		setDriverId(state, action: PayloadAction<string>) {
			state.driver.driverId = action.payload
		},

		setCarClubDriver(state, action) {
			state.carClubDriver = action.payload
		},
		updateDriver(state, action: PayloadAction<DriverListType>) {
			function getNewOrExistingDrivers(type: 'existing' | 'new') {
				return drivers => drivers.filter(driver => (type == 'existing' ? !driver.newDriver : driver.newDriver))
			}

			function formatNewDrivers(drivers) {
				return drivers.map(driver => {
					const { email, exists, newDriver, ...otherProps } = driver

					return { ...otherProps }
				})
			}

			function formatToDriverIds(drivers) {
				return drivers.map(driver => driver.driverId)
			}

			if (action.payload) {
				if (state.driver.drivers.length >= state.driver.maximum) {
					toast.error(`Maximum number of drivers added`)
					state.driver.drivers = [...state.driver.drivers]
				} else if (state.driver.drivers && state.driver.drivers.find(driver => driver?.name === action.payload?.name)) {
					toast.error(`You've already added a driver with that name`)
					state.driver.drivers = [...state.driver.drivers]
				} else {
					toast.success(`${action.payload.name || 'Driver'} was added successfully`)
					state.driver.drivers = [...state.driver.drivers, action.payload]
				}

				state.driver.existingDrivers = compose(formatToDriverIds, getNewOrExistingDrivers('existing'))(state.driver.drivers)
				state.driver.newDrivers = compose(formatNewDrivers, getNewOrExistingDrivers('new'))(state.driver.drivers)
			}
		},
		editDriver(state, action) {
			state.driver.drivers = action.payload
		},
		removeDriver(state, action) {
			state.driver.drivers = state.driver.drivers.filter(driver => {
				if (!action.payload.email) {
					return driver.name !== action.payload.name
				}

				return driver.email !== action.payload.email
			})
			state.driver.existingDrivers = state.driver.drivers.filter(driver => driver.driverId).map(driver => driver.driverId)
			state.driver.newDrivers = state.driver.newDrivers.filter(driver => driver.name != action.payload.name)
			delete state.driver.driversBespokeFields[action.payload.driverid]

			toast.success(`${action.payload.name || action.payload.email} removed from the booking`)
		},
		clearDrivers(state) {
			state.driver.drivers = []
			state.driver.existingDrivers = []

			if (state.driver.newDrivers.length > 0) {
				state.driver.drivers = state.driver.newDrivers
			}
			state.driver.driversBespokeFields = {}
		},
		clearBooker(state) {
			state.booker = {}
		},
		setActiveDropdown(state, action) {
			state.activeDropdown = action.payload
		},
		clearActiveDropdown(state) {
			state.activeDropdown = null
		},
		setSelectedRentalTab(state, action) {
			const { panelCode, initialTab, cardTitle, panesToDisplay } = action.payload

			if (panesToDisplay) {
				const panesToDisplayFormatted = panesToDisplay.map(({ menuItem }) => menuItem.key)
				state.panesToDisplayFormatted = panesToDisplayFormatted
			}

			state.selectedRentalTab.name = panelCode
			state.selectedRentalTab.cardTitle = cardTitle
			state.selectedRentalTab.tariffs = state.availableTariffs[panelCode] || []
			state.tariffBandsAvailable =
				initialTab && !state.account.businessId ? initialTab : Boolean(state.availableTariffs[state.selectedRentalTab.name])
			state.selectedRentalTab.cuiPanelId = state.availableTariffs.panelIds[panelCode]
			state.panelActiveIndex = state.panesToDisplayFormatted?.indexOf(panelCode)
		},
		setPanelIds(state, action) {
			const formattedPanelIds = action.payload.reduce((accum, panel) => {
				accum[panel.key] = panel.cuiPanelId
				return accum
			}, {})

			state.availableTariffs.panelIds = formattedPanelIds
		},
		resetCuiPanelId(state, action) {
			if (action.payload) {
				state.selectedRentalTab.cuiPanelId = ''
			} else {
				state.selectedRentalTab.cuiPanelId = state.availableTariffs.panelIds.dailyRental
			}
		},
		setDriverBespokeFields(state, action) {
			const { driverId, driverBespokeFields } = action.payload

			state.driver.driversBespokeFields[driverId] = formattedDriverBespokeFields(driverBespokeFields).reduce((rows, field, i) => {
				if (i % 2 === 0) {
					rows.push([])
				}
				rows[rows.length - 1].push(field)
				return rows
			}, [])
		},
		addDriverRestrictionVehicleIds(state, action) {
			const { driverId, driverRestrictionVehicleIds } = action.payload

			state.driver.driverRestrictionVehicleIds[driverId] = driverRestrictionVehicleIds
		},
	},
	extraReducers: builder => {
		/*
		 * listen index.php controller and add businessId and businessName
		 */

		builder.addCase(setPoolFleetVehicles, (state, action) => {
			if (!action.payload) {
				state.selectedRentalTab = {
					name: state.selectedRentalTab.name,
					tariffs: state.availableTariffs[state.selectedRentalTab.name],
					cardTitle: state.selectedRentalTab.cardTitle,
					cuiPanelId: state.availableTariffs.panelIds[state.selectedRentalTab.name],
				}
			} else {
				state.selectedRentalTab = {
					name: 'poolFleet',
					tariffs: state.availableTariffs.poolFleet,
					cardTitle: 'Pool Fleet',
					cuiPanelId: state.availableTariffs.panelIds.poolFleet,
				}
			}
		}),
			builder.addCase(fetchIndexDefaults.fulfilled, (state, action) => {
				const { availablePanelTariffs } = action.payload

				state.availableTariffs = {
					panelIds: {},
				}

				for (const panel of availablePanelTariffs) {
					state.availableTariffs = {
						...state.availableTariffs,
						[panel.panelCode]: panel.tariffBands,
						panelIds: { ...state.availableTariffs.panelIds, [panel.panelCode]: panel.id },
					}
				}

				const poolFleetTariff = availablePanelTariffs.find(tariff => tariff.panelCode == 'poolFleet')

				if (poolFleetTariff) {
					state.poolFleetAvailable = poolFleetTariff.tariffBands?.length > 0
				}

				state.selectedRentalTab.tariffs = state.availableTariffs[state.selectedRentalTab.name] || []
				state.selectedRentalTab.cuiPanelId = state.availableTariffs.panelIds[state.selectedRentalTab.name]
				state.tariffBandsAvailable = Boolean(state.availableTariffs[state.selectedRentalTab.name])
			}),
			builder.addCase(fetchInitContext.fulfilled, (state, action) => {
				// if it's operator or
				const { context } = action.payload

				if (context?.driver && context.permissions.reservationLimitedBooker) {
					const { id, name, email } = context.driver
					const preSelectedDriver = {
						name,
						value: id,
						driverId: id,
						email,
						exists: true,
						forename: '',
						title: '',
						surname: '',
						id,
						subtext: '',
						text: '',
					}
					state.driver.drivers.push(preSelectedDriver)
					state.driver.existingDrivers.push(id)
				}

				if (!['operator', 'superuser', 'supplier'].find(role => role === context.userContext)) {
					if (context.memberAreaBusinessSwapperOptions.length > 0) {
						state.account.businessId = action.payload.context.currentBusinessId
						state.account.businessName = action.payload.context.currentBusiness
					}
				}
			}),
			builder.addCase(fetchBusinessDefaults.fulfilled, (state, action) => {
				const { additionalDriverLimit } = action.payload

				if (additionalDriverLimit) {
					state.driver.maximum = additionalDriverLimit + 1
				}
			})
	},
})
export const selectContextDropDowns = (state: RootState) => state.contextDropDowns
export const {
	updateBusiness,
	updateDriver,
	removeDriver,
	editDriver,
	setDriverId,
	addBooker,
	setCarClubDriver,
	clearDrivers,
	updateNewDriverDetails,
	clearBooker,
	setDriversInAddressSearch,
	setSelectedRentalTab,
	addAlertsToDriver,
	setPanelIds,
	setActiveDropdown,
	clearActiveDropdown,
	resetCuiPanelId,
	setDriverBespokeFields,
	addDriverRestrictionVehicleIds,
} = contextDropDownsSlice.actions
export const contextDropDownsSliceReducer = contextDropDownsSlice.reducer
